import { useNavigate } from "@solidjs/router";
import { produce } from "solid-js/store";
import { toast } from "solid-sonner";
import { useI18n } from "@/api/i18n";
import { getApiErrorMessage } from "@/api/utils/error";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { useCreateSoloFreelancerWorkspace } from "../api";
import { useCompanyActions } from "../store/company-store";

const DEFAULT_CREATE_JOB_OFFER_PATH = "/job-offers/create/ai";

type StartPublishingAsFreelancerOptions = {
  replace?: boolean;
  targetPath?: string;
};

export const useStartPublishingAsFreelancer = () => {
  const [authStoreV2, setAuthStoreV2] = useAuthV2();
  const { selectCompany } = useCompanyActions();
  const navigate = useNavigate();
  const createSoloFreelancer = useCreateSoloFreelancerWorkspace();
  const { t } = useI18n();

  const startPublishingAsFreelancer = async (
    options: StartPublishingAsFreelancerOptions = {},
  ) => {
    let response: Awaited<ReturnType<typeof createSoloFreelancer.mutateAsync>>;
    try {
      response = await createSoloFreelancer.mutateAsync({
        name: authStoreV2?.profile?.full_name ?? undefined,
      });
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          t("jobOffers.create.workspaceChoice.freelancer_error_default"),
          t,
        ),
      );
      return null;
    }

    selectCompany(response.context.selected_workspace ?? response.workspace);
    setAuthStoreV2(
      produce((state) => {
        state.companies = response.context.publisher_workspaces;
        state.capabilities = response.context.capabilities;
        state.next_step = response.context.next_step;
        state.active_subscription_id = response.context.active_subscription_id;

        if (!state.profile) {
          return;
        }

        state.profile.id = response.context.profile.id;
        state.profile.email = response.context.profile.email;
        state.profile.full_name = response.context.profile.full_name ?? "";
        state.profile.username = response.context.profile.username;
        state.profile.avatar_url = response.context.profile.avatar_url;
        state.profile.onboarding_status =
          response.context.profile.personal_profile_status;
        state.profile.personal_profile_status =
          response.context.profile.personal_profile_status;
        state.profile.available_from = response.context.profile.available_from;
        state.profile.last_selected_workspace_id =
          response.context.profile.last_selected_workspace_id;
        state.profile.billing_status = response.context.profile.billing_status;
        state.profile.profile_type = response.context.capabilities.can_publish
          ? "provider"
          : "consumer";
      }),
    );
    navigate(options.targetPath ?? DEFAULT_CREATE_JOB_OFFER_PATH, {
      replace: options.replace,
    });

    return response;
  };

  return {
    startPublishingAsFreelancer,
    isPending: () => createSoloFreelancer.isPending,
  };
};
